import React, {useEffect, useState} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import {LinearGradient} from 'react-native-linear-gradient';
import TextGeneric from '../textGeneric';
import {
  LayoutChangeEvent,
  Pressable,
  RefreshControl,
  View,
  ScrollView,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';
import Icon from '../icon';
import Animated, {
  FadeInDown,
  FadeOutDown,
  ZoomIn,
  ZoomOut,
} from 'react-native-reanimated';
import GenericButton from '../button';
import {tr} from '../../translations';
import {getFontFamily} from '../../utils/helpers';
import Loader from '../loader';
import EmptyList from '../emptyList';
import DrugFiltersListSkeleton from '../drugFiltersListSkeleton';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {DrugFilterItemProps} from '../../interfaces/components/drugFilterItem';
import {DrugItemType} from '../../interfaces/api/tabs';
import SkeletonPlaceholder from '../skeletonPlaceholder';
import {colors} from '../../theme/colors';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';

const Wrapper = styled(View)<{isModalOpen: boolean}>`
  z-index: ${({isModalOpen}) => (isModalOpen ? 2 : 1)};
`;
const Container = styled.TouchableOpacity`
  border-radius: 100px;
  align-self: flex-start;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: ${({theme}) => theme.screenBackground};
  shadow-color: ${({theme}) => theme.text.dark};
  shadow-offset: 0 2px;
  shadow-opacity: 0.7;
  shadow-radius: 2px;
  margin: ${({theme}) => theme.text.s12 / 2}px;
  padding-right: 1px;
  padding-bottom: 1px;
  z-index: 3;
`;
const Label = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s8}px;
  color: ${({theme, isModalOpen}) =>
    isModalOpen ? theme.text.light : theme.text.black};
`;
const FilterItemLabel = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s8}px;
  color: ${({theme, isSelected}) =>
    isSelected ? theme.text.light : theme.text.title};
  text-align: center;
`;
const ItemContainer = styled.View<any>`
  width: ${({isLeft}) => (isLeft ? '49%' : '51%')};
  border-right-width: ${({isLeft}) => (isLeft ? 1 : 0)}px;
  border-color: ${({theme}) => theme.primary};
  height: 100%;
  border-bottom-width: 1px;
  border-bottom-color: ${({theme}) => theme.text.grey + '11'};
  background-color: ${({isSelected, theme}) =>
    isSelected ? theme.primary + '77' : 'transparent'};
`;
const FilterItem = styled.TouchableOpacity<any>`
  padding: ${({theme}) => theme.text.s55}px ${({theme}) => theme.text.s6}px;
  min-height: ${({theme}) => theme.text.s8 * 4}px;
`;
const Overlay = styled(Animated.View)<{
  position: any;
  hdp: number;
  wdp: number;
}>`
  height: ${({hdp}) => hdp * 5}px;
  aspect-ratio: 1;
  border-radius: ${({wdp}) => wdp * 5}px;
  position: absolute;
  background-color: ${({theme}) => theme.primary + '44'};
  top: -${({position, hdp}) => hdp * 2 - position?.width / 2}px;
  left: -${({position, hdp}) => hdp * 2 - position?.width / 2}px;
  z-index: 1;
`;
const ModalContainer = styled(Animated.View)<any>`
  height: ${({y, bottomInset}) => (bottomInset || 0) - (y || 0)}px;
  width: ${({width, numberOfItems}) => numberOfItems * width}px;
  position: absolute;
  align-self: center;
  top: ${({height}) => height || 0}px;
  left: ${({width, x, numberOfItems, availableSpace}) => {
    const totalPadding = numberOfItems * width - availableSpace;
    return -totalPadding / 2 - x;
  }}px;
  z-index: 2;
  border-radius: 20px;
`;
const MaskContainer = styled.View<any>`
  height: ${({height}) => height || 0}px;
  width: ${({width}) => width || 0}px;
  position: absolute;
  top: ${({height}) => -height || 0}px;
  left: ${({x, width, availableSpace}) => {
    const totalPadding =
      Math.floor(availableSpace / width) * width - availableSpace;
    return x + totalPadding / 2;
  }}px;
  align-self: center;
  background-color: ${({theme}) => theme.screenBackground};
  border-top-left-radius: 100px;
  border-top-right-radius: 100px;
  align-items: flex-end;
  justify-content: flex-end;
`;
const MaskIconContainer = styled.View<any>`
  position: absolute;
  bottom: -1px;
  ${({isLeft, position, isStart}) =>
    isLeft
      ? `left:${-0.4 * (position?.width || 0) + 1}px;transform: rotate(${
          isStart ? '0' : '-90'
        }deg)`
      : `right:${-0.4 * (position?.width || 0) + 1}px`};
`;
const ModalContentContainer = styled.View<any>`
  height: 100%;
  width: 100%;
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 20px;
  ${({isFirst}) => isFirst && 'border-top-left-radius:0'};
  ${({isLast}) => isLast && 'border-top-right-radius:0'};
  overflow: hidden;
`;
const ConfirmSelection = styled(GenericButton)`
  align-self: center;
  padding: 2px;
  margin-vertical: ${({theme}) => theme.text.s9}px;
  width: 100%;
`;
const EmptyContainer = styled.View<any>`
  align-items: center;
  justify-content: center;
  height: ${({positionY, bottomInset}) =>
    (bottomInset || 0) - (positionY || 0)}px;
`;
const EmptyText = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s9}px;
  color: ${({theme}) => theme.text.dark};
  width: 80%;
  padding-vertical: 4%;
  text-align: center;
`;

const GridRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

/**
 * Drug Filter Item component.
 *
 * @param {DrugFilterItemProps} props - The Drug Filter Item component props.
 * @returns {React.ReactElement} The rendered Fab component.
 */
const DrugFilterItem: React.FC<DrugFilterItemProps> = ({
  style,
  name,
  isSkeleton,
  isLoading,
  data,
  isError,
  onRefreshPress,
  onEndReached,
  selectedDrug,
  onConfirmSelection,
  onPress,
  isFilterModalShouldClose,
  setScrollEnabled,
}: DrugFilterItemProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {hdp, wdp, getByScreenSize} = useResponsiveDimensions();
  const availableSpace = wdp(getByScreenSize(90, 40));
  const [selected, setSelected] = useState(selectedDrug || {});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [position, setPosition] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
    numberOfItems: number;
  }>({
    x: 0,
    y: 0,
    width: 40,
    height: 40,
    numberOfItems: 1,
  });

  useEffect(() => {
    setSelected(selectedDrug);
    setScrollEnabled && setScrollEnabled(!isModalOpen);
  }, [selectedDrug, isModalOpen]);

  useEffect(() => {
    if (isFilterModalShouldClose) {
      setIsModalOpen(false);
    }
  }, [isFilterModalShouldClose]);

  const handleLayout = (event: LayoutChangeEvent) => {
    if (event.nativeEvent.layout) {
      const numberOfItems = Math.floor(
        availableSpace / event.nativeEvent.layout.width,
      );
      setPosition({...event.nativeEvent.layout, numberOfItems});
    }
  };

  const isLastInTheRow = () => {
    const modalSpace = position.numberOfItems * position.width;
    const totalPadding = modalSpace - availableSpace;
    const startPosition = -totalPadding / 2 - position.x;

    return (
      Math.ceil(
        (Math.abs(startPosition) + position.width / 2) / position.width,
      ) === position.numberOfItems
    );
  };

  // Handle scroll to detect when to load more items
  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const {layoutMeasurement, contentOffset, contentSize} = event.nativeEvent;
    const paddingToBottom = 20;
    if (
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - paddingToBottom
    ) {
      // Load more when scrolled to bottom
      onEndReached && onEndReached();
    }
  };

  const renderFilterItem = (item: DrugItemType, index: number) => {
    const isSelected = selected[name || '']?.id === item?.id;
    return (
      <ItemContainer
        key={item.id || index}
        isSelected={isSelected}
        isLeft={index % 2 === 0}>
        <FilterItem
          onPress={() => {
            if (name) {
              setSelected({[name]: item});
            }
          }}>
          <FilterItemLabel isSelected={isSelected}>
            {item?.name || ''}
          </FilterItemLabel>
        </FilterItem>
      </ItemContainer>
    );
  };

  const renderGridItems = () => {
    if (!data || data.length === 0) {
      return _renderEmpty();
    }

    const rows = [];
    // Create rows with 2 items each
    for (let i = 0; i < data.length; i += 2) {
      const rowItems = data.slice(i, i + 2);

      // If odd number of items, add empty placeholder to maintain layout
      if (rowItems.length === 1) {
        rowItems.push({} as DrugItemType);
      }

      rows.push(
        <View key={`row-${i}`}>
          <GridRow>
            {rowItems.map((item, index) => renderFilterItem(item, i + index))}
          </GridRow>
          {i < data.length - 2 && (
            <View
              style={{
                height: 1,
                width: '100%',
                backgroundColor: theme.border + '77',
              }}
            />
          )}
        </View>,
      );
    }

    return rows;
  };

  const _renderFooter = () => (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginVertical: '5%',
      }}>
      {isLoading && (data || []).length > 0 && <Loader color={theme.primary} />}
    </View>
  );

  const _renderEmpty = () =>
    isError ? (
      <EmptyContainer
        bottomInset={hdp(59) - (bottomInset || 0)}
        positionY={position?.y}>
        <EmptyList
          iconName={'alert'}
          iconColor={theme.primary}
          iconSize={theme.text.s1 * 2}
          onRefreshPress={onRefreshPress}
          title={'app.error'}
          showRefresh
        />
      </EmptyContainer>
    ) : isLoading ? (
      <DrugFiltersListSkeleton />
    ) : (
      <EmptyContainer
        bottomInset={hdp(59) - (bottomInset || 0)}
        positionY={position?.y}>
        <View>
          <Icon
            name={'webClock'}
            size={theme.text.s1 * 2}
            color={theme.primary}
          />
        </View>
        <EmptyText semiBold>{tr('drugs.empty')}</EmptyText>
      </EmptyContainer>
    );

  const submit = () => {
    onConfirmSelection && onConfirmSelection(selected);
    setIsModalOpen(false);
  };

  return (
    <Wrapper onLayout={handleLayout} isModalOpen={isModalOpen}>
      <Container
        onPress={() => {
          setIsModalOpen(prev => !prev);
          onPress && onPress();
        }}
        style={style}>
        <LinearGradient
          style={{
            width: theme.text.s1,
            aspectRatio: 1,
            borderRadius: 100,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          colors={[
            selected[name || ''] ? colors.primary : colors.primary + '44',
            selected[name || ''] ? colors.secondary : colors.secondary + '44',
          ]}>
          {isSkeleton ? (
            <SkeletonPlaceholder />
          ) : (
            <Label
              semiBold={isModalOpen || selected[name || '']}
              isModalOpen={isModalOpen || selected[name || '']}>
              {name || ''}
            </Label>
          )}
        </LinearGradient>
      </Container>
      {isModalOpen && (
        <>
          <Overlay
            hdp={hdp(100)}
            wdp={wdp(100)}
            position={position}
            entering={ZoomIn}
            exiting={ZoomOut}>
            <Pressable
              style={{width: '100%', height: '100%'}}
              onPress={() => setIsModalOpen(false)}
            />
          </Overlay>
          <ModalContainer
            availableSpace={availableSpace}
            bottomInset={hdp(65) - (bottomInset || 0)}
            entering={FadeInDown.delay(100)}
            exiting={FadeOutDown}
            {...position}>
            <MaskContainer availableSpace={availableSpace} {...position}>
              {position?.x > position?.width / 2 && (
                <MaskIconContainer position={position} isLeft>
                  <Icon
                    name={'subtract'}
                    size={0.4 * (position?.width || 0)}
                    color={theme.screenBackground}
                  />
                </MaskIconContainer>
              )}
              {!isLastInTheRow() && (
                <MaskIconContainer position={position}>
                  <Icon
                    name={'subtract'}
                    size={0.4 * (position?.width || 0)}
                    color={theme.screenBackground}
                  />
                </MaskIconContainer>
              )}
            </MaskContainer>
            <ModalContentContainer
              isFirst={position?.x < position?.width / 2}
              isLast={isLastInTheRow()}>
              <ScrollView
                style={{flex: 1}}
                contentContainerStyle={{
                  padding: theme.text.s12,
                }}
                onScroll={handleScroll}
                scrollEventThrottle={16}
                refreshControl={
                  <RefreshControl
                    tintColor={theme.secondary}
                    refreshing={!!isLoading && data?.length === 0}
                    onRefresh={onRefreshPress}
                  />
                }>
                {renderGridItems()}
                {_renderFooter()}
              </ScrollView>
              <ConfirmSelection
                onPress={submit}
                disabled={
                  !selected[name || ''] ||
                  selected[name || '']?.id === selectedDrug[name || '']?.id
                }
                outline
                radius={100}
                labelStyle={{
                  color: theme.text.dark,
                  fontFamily: getFontFamily().regular,
                  paddingLeft: theme.text.s6,
                }}
                style={{
                  opacity:
                    !selected[name || ''] ||
                    selected[name || '']?.id === selectedDrug[name || '']?.id
                      ? 0.4
                      : 1,
                }}
                isSmall
                label={tr('drugs.confirmSelection')}
                showRightIcon
                rightIconName={'navArrow'}
                rightIconSize={theme.text.s3}
                rightIconColor={theme.primary}
                rightIconContainerStyle={{position: 'relative', right: 0}}
                rightIconStrokeColor={theme.text.light}
              />
            </ModalContentContainer>
          </ModalContainer>
        </>
      )}
    </Wrapper>
  );
};

export default DrugFilterItem;
